import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { PlusIcon, SettingsSliderIcon } from "../../../../../assets/icons";
import { ExpendableSelector, ExpendableSelectorItem } from "../../../../../common/components/expendable-selector/ExpendableSelector";
import UserProfileImage from "../../../../../common/components/profile-image/UserProfileImage";
import { CardFrame } from "../../../../../common/components/card-frame/CardFrame";
import { SelectableItem } from "../../../../../common/components/selectable-item/SelectableItem";
import CustomerPageLayout from "../../../customer-page-layout/CustomerPageLayout";
import { RootState } from "../../../../../stores/store";
import { useGetAllCustomerMeterNumbersQuery, useActivateCustomerMeterMutation } from "../../../../../service/vending-service";
import { ConsumerType, CustomerMeterNumber } from "../../../../../model/energy-profile";
import { setEnergyProfileInfo } from "../../../../../stores/slices/customer-energy-profile-slice";
import { ApplicationRoutes } from "../../../../../routes";
import { useCan } from "../../../../../guards/useCan";
import { CustomerPermissions } from "../../../../../model/permissions/permissions";
import styles from "./SwitchMeterNumberPage.module.scss";

function getMeterOrAccountNumber(meter: CustomerMeterNumber): string {
	if (meter.consumerType === ConsumerType.Postpaid) return meter.accountNumber;
	if (meter.consumerType === ConsumerType.Prepaid) return meter.meterNumber;
	return "";
}

export default function SwitchMeterNumberPage() {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const userInfo = useSelector((state: RootState) => state.user);
	const fullName = `${userInfo.firstName} ${userInfo.lastName}`;

	const { can } = useCan();
	const canViewMeters = can(CustomerPermissions.ViewMeters);
	const canAddMeter = can(CustomerPermissions.AddMeter);
	const canSetActiveMeter = can(CustomerPermissions.SetActiveMeter);

	const { data: metersData, isLoading: isLoadingMeters } = useGetAllCustomerMeterNumbersQuery();
	const [activateCustomerMeter, activateResult] = useActivateCustomerMeterMutation();
	const [loadingMeterId, setLoadingMeterId] = useState<string | null>(null);
	const pendingMeterRef = useRef<CustomerMeterNumber | null>(null);

	useEffect(() => {
		if (activateResult.isSuccess || activateResult.isError) {
			setLoadingMeterId(null);
		}
		if (activateResult.isSuccess && pendingMeterRef.current) {
			const meter = pendingMeterRef.current;
			dispatch(
				setEnergyProfileInfo({
					meterNumber: meter.meterNumber,
					accountNumber: meter.accountNumber,
					consumerType: meter.consumerType,
					preferRemoteTokenLoad: meter.preferRemoteTokenLoad,
					residencyStatus: meter.residencyStatus,
				})
			);
			pendingMeterRef.current = null;
		}
	}, [activateResult.isSuccess, activateResult.isError]);

	const items: ExpendableSelectorItem<CustomerMeterNumber>[] = (metersData ?? []).map(meter => ({
		id: meter.id,
		title: meter.name,
		subtitle: getMeterOrAccountNumber(meter),
		isSelected: meter.isActive,
		isLoading: meter.id === loadingMeterId,
		data: meter,
	}));

	const handleSelect = (item: ExpendableSelectorItem<CustomerMeterNumber>) => {
		if (!canSetActiveMeter) return;
		if (item.isSelected) return;
		pendingMeterRef.current = item.data ?? null;
		setLoadingMeterId(item.id);
		activateCustomerMeter(item.id);
	};

	return (
		<CustomerPageLayout
			title={t("common.labels.energyProfile")}
			decreaseMainContentPadding
		>
			<div className={styles.content}>
				<UserProfileImage customer={userInfo} size={70} />
				<span className={styles.name}>{fullName}</span>
				<span className={styles.phone}>{userInfo.phoneNumber}</span>

				<div className={styles.selector}>
					<ExpendableSelector
						title={t("energyManagement.energyProfile.switchMeterNumber")}
						items={isLoadingMeters || !canViewMeters ? [] : items}
						onItemSelected={handleSelect}
						bottomActionText={canAddMeter ? t("energyManagement.energyProfile.addMeterNumber") : undefined}
						bottomActionIcon={canAddMeter ? <PlusIcon /> : undefined}
						onBottomActionPressed={canAddMeter ? () => navigate(`/${ApplicationRoutes.SETTINGS}/${ApplicationRoutes.CUSTOMER_ENERGY_PROFILE}/${ApplicationRoutes.ADD_METER}`) : undefined}
					/>
				</div>

				<div className={styles.manageMeters}>
					<CardFrame>
						<SelectableItem
							leftIcon={<SettingsSliderIcon />}
							title={t("energyManagement.energyProfile.manageMeters")}
							subtitle=""
							hideBottomBorder
							onClick={() => navigate(`/${ApplicationRoutes.SETTINGS}/${ApplicationRoutes.CUSTOMER_ENERGY_PROFILE}/${ApplicationRoutes.CUSTOMER_MANAGE_METERS}`)}
						/>
					</CardFrame>
				</div>
			</div>
		</CustomerPageLayout>
	);
}
